import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { AuthContext } from '../../context/authContextCore'
import { ToastContext } from '../../context/toastContextCore'
import { useEditLock } from '../../hooks/useEditLock'
import { usePushBackHandler } from '../../context/BackStackContext'
import { isHelperOnlyAccount } from '../../utils/helperMembership'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BrushRoundedIcon from '@mui/icons-material/BrushRounded'
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded'
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { getFormDefinition } from '../../services/formsService'
import type { ChecklistItem, ChecklistWorkflowStatus } from '../../types/schema'
import type { FormDefinition } from '../../types/forms'
import type { DesignAsset, DesignEmbedSnapshot } from '../../types/design'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import { resolveAllowedTransitions } from '../../services/helper/helperTaskService'
import { DependencyLockChip, MissedPeriodsChip } from '../Common'
import {
  deleteHelperComment,
  editHelperComment,
  submitHelperComment,
  submitHelperProof,
  submitHelperTransition,
  subscribeChecklistComments,
  subscribeChecklistProofs,
  type HelperChecklistAttachment,
  type HelperChecklistComment,
  type HelperChecklistProof,
} from '../../services/helper/helperChecklistDetailsService'
import {
  normalizeChecklistBlockedTodoPolicy,
  type ChecklistBlockedTodoPolicy,
} from '../../utils/checklistBlockedTodoPolicy'
import { buildLockGraph, resolveLock } from '../../services/checklistLockService'
import { displayNameOrMaskedPhone } from '../../utils/phoneNumber'
import { subscribeChecklistItemProjection } from '../../services/checklistReader'
import { subscribeToBookPages } from '../../services/checklistBookService'
import { applyStripboardDelta, ChecklistBlockerError } from '../../services/stripboardDeltaService'
import { updateChecklistWorkflowStatus } from '../../services/checklistStatusWriter'
import {
  canPickInDropdown,
  evaluateBlockers,
  type BlockerEntry,
} from '../../services/checklistStatusMachine'
import { ctaSectionFor } from '../../types/checklistBlockerCodes'
import { StatusBlockerBanner } from '../DrawingBoard/StatusBlockerBanner'
import { bookIdFor, type ChecklistBookPage } from '../../types/checklistBooks'
import { currentOccurrenceKeyForItem } from '../../services/recurringChecklistService'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import {
  buildMobileChecklistOccurrenceOptions,
  getMobileRecurringMode,
} from '../ChecklistBoard/mobileChecklistOccurrenceUtils'
import {
  AttachmentPreviewDialog,
  ConfirmActionModal,
  Button,
  IconButton,
  Pressable,
  HiddenFileInput,
  MiniLoader,
  Select,
  Textarea,
  TextInput,
  type AttachmentPreviewItem,
} from '../Common'
import { FloatingInput } from '../Common/FloatingInput'
import { inferAttachmentPreviewKind } from '../Common/attachmentPreviewHelpers'
import { FormExperienceScreen } from '../Forms/FormExperienceScreen'
import { DesignSnapshotRenderer } from '../Design/DesignSnapshotRenderer'
import { serializeDesignFromAsset } from '../../services/designEmbedService'
import { getDesignAsset } from '../../services/designAssetService'
import { subscribeFieldPassDelegationGrant } from '../../services/fieldPassService'
import { useNls } from '../../nls/nlsProvider'
import { useWorkspaceDraft } from '../../hooks/useWorkspaceDraft'
import { QuickDelegateSheet } from '../FieldPass/QuickDelegateSheet'
import { HelperReadinessStrip } from './HelperReadinessStrip'
import type { HelperReadinessModel } from './helperReadinessModel'
import {
  parseExpenseAmountToMinor,
  submitPendingExpense,
} from '../../services/helper/helperExpenseIntakeService'

const STATUS_LABEL: Record<ChecklistWorkflowStatus, string> = {
  CREATED: 'Created',
  ASSIGNED: 'Assigned',
  ACCEPTED: 'Accepted',
  ASSESSING: 'Assessing',
  ONGOING: 'Ongoing',
  COMPLETED: 'Completed',
  NOT_REQUIRED: 'Not required',
  POSTPONED: 'Postponed',
}

const STATUS_TONE: Record<ChecklistWorkflowStatus, string> = {
  CREATED: 'text-[var(--muted)]',
  ASSIGNED: 'text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]',
  ACCEPTED: 'text-[color-mix(in_srgb,var(--accent)_60%,var(--text-strong))]',
  ASSESSING: 'text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]',
  ONGOING: 'text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]',
  COMPLETED: 'text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]',
  NOT_REQUIRED: 'text-[var(--muted)]',
  POSTPONED: 'text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]',
}

const MAX_PROOF_BYTES = 10 * 1024 * 1024
const FORM_CACHE_TTL_MS = 5 * 60_000
const DESIGN_CACHE_TTL_MS = 5 * 60_000

type Actor = { id: string; name: string }

type ActorRole = 'HELPER' | 'MEMBER'

type Props = {
  item: HelperAssignedItem | null
  actor: Actor | null
  onClose: () => void
  blockedTodoPolicy?: ChecklistBlockedTodoPolicy
  initialOccurrenceKey?: string | null
  actorRole?: ActorRole
  readiness?: HelperReadinessModel | null
  onReportBlocker?: (item: HelperAssignedItem) => void
  onRequestReplenishment?: (item: HelperAssignedItem) => void
  checklistItems?: readonly ChecklistItem[]
}

type CacheEntry<T> = { loadedAt: number; items: T[] }

type HelperDesignPreview = {
  id: string
  asset: DesignAsset
  snapshot: DesignEmbedSnapshot | null
}

const commentsCache = new Map<string, CacheEntry<HelperChecklistComment>>()
const proofsCache = new Map<string, CacheEntry<HelperChecklistProof>>()
const formsCache = new Map<string, CacheEntry<FormDefinition>>()
const designsCache = new Map<string, CacheEntry<HelperDesignPreview>>()

const formatDate = (value?: string | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const formatDateTime = (value?: number | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString()
}

const formatTimeWindow = (from?: string | null, to?: string | null) => {
  if (!from && !to) return null
  return [from, to].filter(Boolean).join(' – ')
}

const formatBytes = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return ''
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`
  if (value >= 1024) return `${Math.round(value / 1024)} KB`
  return `${value} B`
}

const formatRefreshLabel = (value?: number | null) => {
  if (!value) return 'Not synced yet'
  const delta = Date.now() - value
  if (delta < 15_000) return 'Synced just now'
  if (delta < 60_000) return 'Synced under 1 min ago'
  if (delta < 60 * 60_000) return `Synced ${Math.max(1, Math.round(delta / 60_000))} min ago`
  return `Synced ${Math.max(1, Math.round(delta / (60 * 60_000)))} hr ago`
}

const isCacheFresh = (loadedAt: number | undefined, ttlMs: number) =>
  typeof loadedAt === 'number' && Date.now() - loadedAt < ttlMs

const readCurrentCoordinates = (): Promise<{ lat: number; lng: number }> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Location capture is not available on this device.'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => reject(new Error('Location capture failed. Allow device location access and retry.')),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 12_000 },
    )
  })

const toRadians = (value: number) => (value * Math.PI) / 180

const getDistanceMeters = (
  start: { lat: number; lng: number },
  end: { lat: number; lng: number },
): number => {
  const earthRadius = 6_371_000
  const deltaLat = toRadians(end.lat - start.lat)
  const deltaLng = toRadians(end.lng - start.lng)
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(toRadians(start.lat)) * Math.cos(toRadians(end.lat)) * Math.sin(deltaLng / 2) ** 2
  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const loadFormDefinitions = async (formIds: readonly string[]): Promise<FormDefinition[]> => {
  const dedupedIds = Array.from(new Set(formIds.filter((formId): formId is string => typeof formId === 'string' && formId.trim().length > 0)))
  if (dedupedIds.length === 0) return []
  const defs = await Promise.all(dedupedIds.map((formId) => getFormDefinition(formId)))
  return defs.filter((def): def is FormDefinition => Boolean(def))
}

const loadDesignPreviews = async (designIds: readonly string[]): Promise<HelperDesignPreview[]> => {
  const dedupedIds = Array.from(new Set(designIds.filter((designId): designId is string => typeof designId === 'string' && designId.trim().length > 0)))
  if (dedupedIds.length === 0) return []
  const assets = await Promise.all(dedupedIds.map((designId) => getDesignAsset(designId)))
  return assets.flatMap((asset) => {
    if (!asset) return []
    return [{
      id: asset.id,
      asset,
      snapshot: asset.shapes.length > 0 ? serializeDesignFromAsset(asset, []) : null,
    }]
  })
}

const buildPreviewItem = (
  attachment: HelperChecklistAttachment | HelperChecklistProof,
): AttachmentPreviewItem | null => {
  const url = 'downloadUrl' in attachment
    ? attachment.downloadUrl
    : attachment.fileUrl
  if (!url) return null
  const label = 'name' in attachment
    ? attachment.name
    : attachment.fileName ?? 'Attachment'
  return {
    id: attachment.id,
    kind: inferAttachmentPreviewKind({
      name: label,
      contentType: 'contentType' in attachment ? (attachment.contentType ?? undefined) : undefined,
    }),
    label,
    mimeType: 'contentType' in attachment ? (attachment.contentType ?? undefined) : undefined,
    sizeLabel: 'size' in attachment ? formatBytes(attachment.size) : undefined,
    url,
    downloadUrl: url,
  }
}

const extractEscalationBody = (value: string) => value.replace(/^\[ESCALATION]\s*/i, '')

const MetricPill = ({
  icon,
  label,
  ariaLabel,
  testId,
}: {
  icon: ReactNode
  label: string
  ariaLabel?: string
  testId?: string
}) => (
  <div
    className="inline-flex min-w-0 items-center gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--text-strong)]"
    aria-label={ariaLabel}
    data-testid={testId}
  >
    <span className="text-[var(--muted)] shrink-0">{icon}</span>
    <span className="truncate">{label}</span>
  </div>
)

const statusIconFor = (status: ChecklistWorkflowStatus): ReactNode => {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircleRoundedIcon style={{ fontSize: 16 }} />
    case 'ONGOING':
    case 'ASSESSING':
      return <ScheduleRoundedIcon style={{ fontSize: 16 }} />
    default:
      return <FactCheckRoundedIcon style={{ fontSize: 16 }} />
  }
}

const SectionPanel = ({
  id,
  icon,
  title,
  subtitle,
  badge,
  loading = false,
  children,
}: {
  id?: string
  icon: ReactNode
  title: string
  subtitle: string
  badge?: ReactNode
  loading?: boolean
  children: ReactNode
}) => (
  <section
    id={id}
    className="overflow-hidden rounded-[28px] border border-[var(--border-subtle)] bg-[var(--surface-card)]"
  >
    <div className="flex w-full items-start justify-between gap-3 px-4 py-4 text-left">
      <div className="flex min-w-0 items-start gap-3">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] text-[var(--text-strong)]"
        >
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[var(--text-strong)]">{title}</h3>
          <p className="mt-1 text-xs leading-relaxed text-[var(--muted)]">{subtitle}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {loading ? <MiniLoader size="xs" inline label="Loading" /> : badge}
      </div>
    </div>
    <div className="border-t border-[var(--border-subtle)] px-4 pb-4 pt-3">
      {children}
    </div>
  </section>
)

export const ChecklistDetailsModal = ({ item, actor, onClose, blockedTodoPolicy, initialOccurrenceKey = null, actorRole = 'HELPER', readiness = null, onReportBlocker, onRequestReplenishment, checklistItems }: Props) => {
  const { t } = useNls()
  const authContext = useContext(AuthContext)
  const userProfile = authContext?.userProfile
  const currentUser = authContext?.currentUser
  const toastContext = useContext(ToastContext)
  const showToast = toastContext?.showToast

  const isHelper = isHelperOnlyAccount({ userProfile, currentUser })

  const editLock = useEditLock(item?.id ?? null, {
    workspaceId: item?.workspaceId ?? null,
    actor: actor ? { uid: actor.id, displayName: actor.name || 'A helper' } : null,
    isAdmin: false,
    enabled: !isHelper,
    onLockConflict: (holder) => {
      showToast?.(`${holder} is editing this item.`, 'info')
    },
  })

  const withLock = useCallback(
    <Args extends readonly unknown[], R>(fn: (...args: Args) => Promise<R> | R) => {
      return async (...args: Args): Promise<R | undefined> => {
        if (isHelper) {
          return await fn(...args)
        }
        const ok = await editLock.acquireOnEdit()
        if (!ok) return undefined
        const result = await fn(...args)
        editLock.markActivity()
        return result
      }
    },
    [editLock, isHelper],
  )

  useEffect(() => {
    if (isHelper) return
    editLock.armIdleRelease(true)
  }, [editLock, isHelper])

  const handleClose = () => {
    if (!isHelper) {
      void editLock.release()
    }
    onClose()
  }

  usePushBackHandler(handleClose)

  const { timeZone, todayIso, weekKey } = useWorkspaceDay(item?.workspaceId)
  const recurringMode = getMobileRecurringMode(item)
  const [selectedOccurrenceKey, setSelectedOccurrenceKey] = useState<string | null>(() => {
    if (initialOccurrenceKey) return initialOccurrenceKey
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const day = params.get('day')
      const week = params.get('week')
      if (day) return day
      if (week) return week
    }
    return item && recurringMode ? currentOccurrenceKeyForItem(item, new Date(), timeZone) : null
  })
  const [occurrencePages, setOccurrencePages] = useState<ChecklistBookPage[]>([])
  const [projectedItem, setProjectedItem] = useState<HelperAssignedItem | null>(null)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingCommentDraft, setEditingCommentDraft] = useState('')
  const [commentToDelete, setCommentToDelete] = useState<HelperChecklistComment | null>(null)
  const [pendingStatus, setPendingStatus] = useState<ChecklistWorkflowStatus | null>(null)
  const [submittingStatus, setSubmittingStatus] = useState<ChecklistWorkflowStatus | null>(null)
  const [serverBlockers, setServerBlockers] = useState<BlockerEntry[]>([])
  const isCommittingPendingRef = useRef(false)
  const deviceFileInputRef = useRef<HTMLInputElement>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [escalateOpen, setEscalateOpen] = useState(false)
  const [escalateDraft, setEscalateDraft] = useState('')
  const [escalateBusy, setEscalateBusy] = useState(false)
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [receiptAmount, setReceiptAmount] = useState('')
  const [receiptCurrency, setReceiptCurrency] = useState('USD')
  const [receiptNote, setReceiptNote] = useState('')
  const [receiptBusy, setReceiptBusy] = useState(false)
  const [fieldPassDelegateOpen, setFieldPassDelegateOpen] = useState(false)
  const [canDelegateFieldPass, setCanDelegateFieldPass] = useState(false)
  const [postingComment, setPostingComment] = useState(false)
  const [comments, setComments] = useState<HelperChecklistComment[]>([])
  const [proofs, setProofs] = useState<HelperChecklistProof[]>([])
  const [forms, setForms] = useState<FormDefinition[]>([])
  const [designs, setDesigns] = useState<HelperDesignPreview[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [proofsLoading, setProofsLoading] = useState(false)
  const [formsLoading, setFormsLoading] = useState(false)
  const [designsLoading, setDesignsLoading] = useState(false)
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null)
  const [previewItem, setPreviewItem] = useState<AttachmentPreviewItem | null>(null)
  const [activeDesignPreview, setActiveDesignPreview] = useState<HelperDesignPreview | null>(null)
  const [commentsSyncToken, setCommentsSyncToken] = useState(0)
  const [proofsSyncToken, setProofsSyncToken] = useState(0)
  const [formsSyncToken, setFormsSyncToken] = useState(0)
  const [designsSyncToken, setDesignsSyncToken] = useState(0)
  const commentsConsumedTokenRef = useRef(0)
  const proofsConsumedTokenRef = useRef(0)
  const formsConsumedTokenRef = useRef(0)
  const designsConsumedTokenRef = useRef(0)

  useEffect(() => {
    if (!item || !recurringMode) {
      setSelectedOccurrenceKey(null)
      setOccurrencePages([])
      setProjectedItem(null)
      return
    }
    const urlKey = typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('day') ?? new URLSearchParams(window.location.search).get('week'))
      : null
    setSelectedOccurrenceKey(initialOccurrenceKey ?? urlKey ?? currentOccurrenceKeyForItem(item, new Date(), timeZone))
  }, [initialOccurrenceKey, item, recurringMode, timeZone])

  useEffect(() => {
    if (!item || !recurringMode) return
    return subscribeToBookPages(bookIdFor(item.workspaceId, recurringMode), setOccurrencePages)
  }, [item, recurringMode])

  useEffect(() => {
    if (!item || !recurringMode || !selectedOccurrenceKey) {
      setProjectedItem(null)
      return
    }
    return subscribeChecklistItemProjection(
      { itemId: item.id, pageKey: selectedOccurrenceKey },
      (next) => {
        if (!next) {
          setProjectedItem(null)
          return
        }
        setProjectedItem({
          ...item,
          ...next,
          effectiveStatus: (next.workflowStatus ?? item.effectiveStatus ?? 'ASSIGNED') as ChecklistWorkflowStatus,
        })
      },
    )
  }, [item, recurringMode, selectedOccurrenceKey])

  const occurrenceOptions = useMemo(
    () => buildMobileChecklistOccurrenceOptions(item, occurrencePages, new Date(), undefined, timeZone),
    [item, occurrencePages, timeZone],
  )

  useEffect(() => {
    if (!recurringMode || occurrenceOptions.length === 0) return
    if (selectedOccurrenceKey && occurrenceOptions.some((option) => option.key === selectedOccurrenceKey)) return
    setSelectedOccurrenceKey(
      occurrenceOptions.find((option) => option.kind === 'current')?.key
      ?? occurrenceOptions[0]?.key
      ?? null,
    )
  }, [occurrenceOptions, recurringMode, selectedOccurrenceKey])

  useEffect(() => {
    setEditingCommentId(null)
    setEditingCommentDraft('')
    setSelectedFormId(null)
    setPreviewItem(null)
    setActiveDesignPreview(null)
    setErrorMessage(null)
    setReceiptFile(null)
    setReceiptAmount('')
    setReceiptNote('')
  }, [item?.id])

  const activeItem = projectedItem ?? item
  const helperCommentKey = activeItem?.id ? `draft.helperComment.${activeItem.id}` : ''
  const [commentDraft, setCommentDraft, clearCommentDraft] = useWorkspaceDraft(helperCommentKey)
  const activeAllowedTransitions = activeItem ? resolveAllowedTransitions(activeItem) : []
  const currentStatus = (activeItem?.workflowStatus ?? activeItem?.effectiveStatus ?? 'ASSIGNED') as ChecklistWorkflowStatus
  const policy = normalizeChecklistBlockedTodoPolicy(
    blockedTodoPolicy ? { checklistBlockedTodoPolicy: blockedTodoPolicy } : null,
  )
  const lock = useMemo(() => {
    if (!activeItem) return null
    const graph = buildLockGraph(checklistItems ?? [activeItem])
    const key = selectedOccurrenceKey ?? (activeItem.checklistType === 'WEEKLY' ? weekKey : todayIso)
    return resolveLock(activeItem, key, graph, occurrencePages)
  }, [activeItem, checklistItems, occurrencePages, selectedOccurrenceKey, todayIso, weekKey])

  const dependencyBlocked = lock ? (lock.blockerCount > 0) : (activeItem?.dependencyBlocked === true)
  const completionLocked = dependencyBlocked && !policy.allowCompletion
  const proofLocked = dependencyBlocked && !policy.allowProof
  const statusLocked = dependencyBlocked && !policy.allowStatusChange
  const commentLocked = dependencyBlocked && !policy.allowComment
  const canEdit = Boolean(actor)
  const inputsDisabled = !canEdit || Boolean(editLock.holder && !editLock.heldByMe)
  const scheduleWindow = [
    formatDate(activeItem?.scheduleStartDate ?? activeItem?.startDate),
    formatDate(activeItem?.scheduleEndDate ?? activeItem?.endDate),
  ]
    .filter(Boolean)
    .join(' → ') || null
  const timeWindow = formatTimeWindow(activeItem?.scheduleTimeFrom, activeItem?.scheduleTimeTo)
  const proofRequired = Boolean(activeItem?.requiresProofFile)
  const locationRequired = Boolean(activeItem?.requiresLocationPin)
  const coSignRequired = Boolean(activeItem?.requiresCoSign)
  const usingProjectedOccurrence = Boolean(recurringMode && selectedOccurrenceKey)
  const activeOccurrenceMeta = occurrenceOptions.find((option) => option.key === selectedOccurrenceKey) ?? null

  const evalTarget = useMemo(() => {
    if (!activeItem) return null
    const incompleteDepIds = lock?.isLocked
      ? (lock.reasons.map((r) => r.targetId).filter(Boolean) as string[])
      : []
    return {
      ...activeItem,
      dependencyAlertNodeIds: incompleteDepIds,
      proofFileUrl: activeItem.proofFileUrl || (proofs.length > 0 ? proofs[0].fileUrl : null),
      proofSubmissionId: activeItem.proofSubmissionId || (proofs.length > 0 ? proofs[0].id : null),
    }
  }, [activeItem, lock, proofs])

  const pendingBlockers = useMemo<BlockerEntry[]>(() => {
    if (!pendingStatus || !evalTarget) return []
    const clientEval = evaluateBlockers(evalTarget, actor ? { uid: actor.id, role: actorRole } : null, pendingStatus)
    if (!clientEval.ok) {
      return clientEval.blockers
    }
    if (serverBlockers.length > 0) {
      return serverBlockers
    }
    return []
  }, [actor, actorRole, evalTarget, pendingStatus, serverBlockers])

  useEffect(() => {
    if (!activeItem?.workspaceId || !actor?.id || actorRole !== 'HELPER') {
      setCanDelegateFieldPass(false)
      setFieldPassDelegateOpen(false)
      return
    }
    return subscribeFieldPassDelegationGrant(activeItem.workspaceId, actor.id, setCanDelegateFieldPass)
  }, [activeItem?.workspaceId, actor?.id, actorRole])

  const formsCacheKey = useMemo(
    () => Array.from(new Set((activeItem?.attachedFormIds ?? []).filter(Boolean))).join('|'),
    [activeItem?.attachedFormIds],
  )
  const designsCacheKey = useMemo(
    () => `${activeItem?.workspaceId ?? ''}|${Array.from(new Set((activeItem?.attachedDesignIds ?? []).filter(Boolean))).join('|')}`,
    [activeItem?.attachedDesignIds, activeItem?.workspaceId],
  )
  const activeForm = forms.find((entry) => entry.id === selectedFormId) ?? null
  const canMarkDone = activeAllowedTransitions.includes('COMPLETED') && !completionLocked && !inputsDisabled

  useEffect(() => {
    if (!activeItem) return
    setReceiptCurrency(activeItem.currencyCode || 'USD')
  }, [activeItem?.currencyCode, activeItem?.id])

  useEffect(() => {
    if (!activeItem) {
      setComments([])
      setCommentsLoading(false)
      return
    }
    const cacheKey = activeItem.id
    const cached = commentsCache.get(cacheKey)
    setComments(cached?.items ?? [])
    setCommentsLoading(false)
    commentsConsumedTokenRef.current = commentsSyncToken
  }, [activeItem?.id, commentsSyncToken])

  useEffect(() => {
    if (!activeItem) return
    const cacheKey = activeItem.id
    const cached = commentsCache.get(cacheKey)
    setCommentsLoading(!cached)
    const stop = subscribeChecklistComments(cacheKey, activeItem.workspaceId, (next) => {
      commentsCache.set(cacheKey, { loadedAt: Date.now(), items: next })
      setComments(next)
      setCommentsLoading(false)
      commentsConsumedTokenRef.current = commentsSyncToken
    })
    return () => stop()
  }, [activeItem?.id, activeItem?.workspaceId, commentsSyncToken])

  useEffect(() => {
    if (!activeItem) {
      setProofs([])
      setProofsLoading(false)
      return
    }
    const cacheKey = activeItem.id
    const cached = proofsCache.get(cacheKey)
    setProofs(cached?.items ?? [])
    setProofsLoading(false)
    proofsConsumedTokenRef.current = proofsSyncToken
  }, [activeItem?.id, proofsSyncToken])

  useEffect(() => {
    if (!activeItem) return
    const cacheKey = activeItem.id
    const cached = proofsCache.get(cacheKey)
    setProofsLoading(!cached)
    const stop = subscribeChecklistProofs(cacheKey, (next) => {
      proofsCache.set(cacheKey, { loadedAt: Date.now(), items: next })
      setProofs(next)
      setProofsLoading(false)
      proofsConsumedTokenRef.current = proofsSyncToken
    })
    return () => stop()
  }, [activeItem?.id, proofsSyncToken])

  useEffect(() => {
    const cached = formsCacheKey ? formsCache.get(formsCacheKey) : null
    setForms(cached?.items ?? [])
    setFormsLoading(false)
    formsConsumedTokenRef.current = formsSyncToken
  }, [formsCacheKey, formsSyncToken])

  useEffect(() => {
    if (!activeItem) return
    if (!formsCacheKey) return
    const cached = formsCache.get(formsCacheKey)
    const shouldRefresh = formsConsumedTokenRef.current !== formsSyncToken
      || !isCacheFresh(cached?.loadedAt, FORM_CACHE_TTL_MS)
    if (!shouldRefresh) return
    setFormsLoading(true)
    void loadFormDefinitions(activeItem.attachedFormIds ?? [])
      .then((next) => {
        formsCache.set(formsCacheKey, { loadedAt: Date.now(), items: next })
        setForms(next)
        formsConsumedTokenRef.current = formsSyncToken
      })
      .catch((error) => {
        setErrorMessage(error instanceof Error ? error.message : 'Unable to load forms.')
      })
      .finally(() => setFormsLoading(false))
  }, [activeItem?.attachedFormIds, formsCacheKey, formsSyncToken])

  useEffect(() => {
    if (!selectedFormId) return
    if (forms.some((entry) => entry.id === selectedFormId)) return
    setSelectedFormId(forms[0]?.id ?? null)
  }, [forms, selectedFormId])

  useEffect(() => {
    const cached = designsCacheKey ? designsCache.get(designsCacheKey) : null
    setDesigns(cached?.items ?? [])
    setDesignsLoading(false)
    designsConsumedTokenRef.current = designsSyncToken
  }, [designsCacheKey, designsSyncToken])

  useEffect(() => {
    if (!activeItem) return
    if (!designsCacheKey) return
    const cached = designsCache.get(designsCacheKey)
    const shouldRefresh = designsConsumedTokenRef.current !== designsSyncToken
      || !isCacheFresh(cached?.loadedAt, DESIGN_CACHE_TTL_MS)
    if (!shouldRefresh) return
    setDesignsLoading(true)
    void loadDesignPreviews(activeItem.attachedDesignIds ?? [])
      .then((next) => {
        designsCache.set(designsCacheKey, { loadedAt: Date.now(), items: next })
        setDesigns(next)
        designsConsumedTokenRef.current = designsSyncToken
      })
      .catch((error) => {
        setErrorMessage(error instanceof Error ? error.message : 'Unable to load designs.')
      })
      .finally(() => setDesignsLoading(false))
  }, [activeItem?.attachedDesignIds, designsCacheKey, designsSyncToken])

  const handleStatus = withLock(async (next: ChecklistWorkflowStatus) => {
    if (!item || !activeItem) return
    if (!actor) {
      setErrorMessage('Helper identity unavailable. Try reopening the app.')
      return
    }
    if (statusLocked || (next === 'COMPLETED' && completionLocked)) {
      const blockerTitle = lock?.reasons?.find((r) => r.title)?.title
      const msg = blockerTitle
        ? `Blocked: ${blockerTitle}`
        : (next === 'COMPLETED' ? 'Blocked by dependency. Completion is disabled.' : 'Blocked by dependency. Status changes are disabled.')
      setErrorMessage(msg)
      return
    }

    if (evalTarget) {
      const clientEval = evaluateBlockers(evalTarget, { uid: actor.id, role: actorRole }, next)
      if (!clientEval.ok) {
        setPendingStatus(next)
        setServerBlockers([])
        return
      }
    }

    setSubmittingStatus(next)
    setErrorMessage(null)
    try {
      if (usingProjectedOccurrence || (next === 'COMPLETED' && locationRequired)) {
        const coordinates = next === 'COMPLETED' && locationRequired && !activeItem.verifiedCoordinates
          ? await readCurrentCoordinates()
          : null
        if (coordinates && activeItem.targetCoordinates && getDistanceMeters(activeItem.targetCoordinates, coordinates) > 50) {
          throw new Error('Verification rejected: you must be physically on-site to mark this complete.')
        }
        await updateChecklistWorkflowStatus({
          item: {
            id: activeItem.id,
            boardStatus: activeItem.boardStatus,
            recordVersion: activeItem.recordVersion,
            workflowStatus: currentStatus,
            workspaceId: activeItem.workspaceId,
            checklistType: activeItem.checklistType,
          },
          workflowStatus: next,
          coordinates,
          actor: { id: actor.id, name: actor.name ?? '' },
          periodKey: recurringMode ? selectedOccurrenceKey : null,
          periodBoardStatus: recurringMode && selectedOccurrenceKey ? activeItem.occurrences?.[selectedOccurrenceKey]?.boardStatus : undefined,
          pageKey: recurringMode ? selectedOccurrenceKey : null,
          runDelta: applyStripboardDelta,
        })
      } else {
        await submitHelperTransition({
          item: {
            id: activeItem.id,
            workspaceId: activeItem.workspaceId,
            parentContainerId: activeItem.parentContainerId,
            workflowStatus: currentStatus,
            isCompleted: activeItem.isCompleted,
            allowedTransitions: (activeItem as unknown as { allowedTransitions?: Partial<Record<ChecklistWorkflowStatus, ChecklistWorkflowStatus[]>> }).allowedTransitions ?? null,
            updatedAtIso: activeItem.updatedAtIso ?? null,
          },
          actor,
          next,
          pageKey: recurringMode ? selectedOccurrenceKey : null,
          actorRole,
        })
      }
      setPendingStatus(null)
      setServerBlockers([])
    } catch (error) {
      if (error instanceof ChecklistBlockerError) {
        setPendingStatus(next)
        setServerBlockers(
          error.blockers.map((b) => ({
            code: b.code,
            message: b.message,
            ctaSection: ctaSectionFor(b.code),
          })),
        )
        return
      }
      setErrorMessage(error instanceof Error ? error.message : 'Unable to transition status.')
    } finally {
      setSubmittingStatus(null)
    }
  })

  useEffect(() => {
    if (!pendingStatus || pendingBlockers.length > 0 || submittingStatus) return
    if (isCommittingPendingRef.current) return
    if (!actor || statusLocked) return

    let isMounted = true
    isCommittingPendingRef.current = true

    void (async () => {
      try {
        await handleStatus(pendingStatus)
      } finally {
        if (isMounted) {
          isCommittingPendingRef.current = false
        }
      }
    })()

    return () => {
      isMounted = false
    }
  }, [actor, handleStatus, pendingBlockers.length, pendingStatus, statusLocked, submittingStatus])

  if (!item || !activeItem) return null

  const handleComment = withLock(async () => {
    if (!actor || !commentDraft.trim() || commentLocked) return
    setPostingComment(true)
    setErrorMessage(null)
    try {
      await submitHelperComment({
        item: {
          id: activeItem.id,
          workspaceId: activeItem.workspaceId,
          parentContainerId: activeItem.parentContainerId,
          updatedAtIso: activeItem.updatedAtIso ?? null,
        },
        actor,
        body: commentDraft.trim(),
        pageKey: recurringMode ? selectedOccurrenceKey : null,
        actorRole,
      })
      clearCommentDraft()
      commentsCache.delete(activeItem.id)
      setCommentsSyncToken((current) => current + 1)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to post comment.')
    } finally {
      setPostingComment(false)
    }
  })

  const handleSaveCommentEdit = withLock(async () => {
    if (!actor || !editingCommentId || !editingCommentDraft.trim() || commentLocked) return
    setPostingComment(true)
    setErrorMessage(null)
    try {
      await editHelperComment({
        commentId: editingCommentId,
        actorId: actor.id,
        body: editingCommentDraft.trim(),
      })
      setEditingCommentId(null)
      setEditingCommentDraft('')
      commentsCache.delete(activeItem.id)
      setCommentsSyncToken((current) => current + 1)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to save comment.')
    } finally {
      setPostingComment(false)
    }
  })

  const handleDeleteComment = withLock(async (comment: HelperChecklistComment) => {
    if (!actor || comment.actorId !== actor.id || commentLocked) return
    setPostingComment(true)
    setErrorMessage(null)
    try {
      await deleteHelperComment({ commentId: comment.id, actorId: actor.id })
      if (editingCommentId === comment.id) {
        setEditingCommentId(null)
        setEditingCommentDraft('')
      }
      commentsCache.delete(activeItem.id)
      setCommentsSyncToken((current) => current + 1)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to delete comment.')
    } finally {
      setPostingComment(false)
    }
  })

  const handleProof = withLock(async (file: File) => {
    if (!actor) return
    if (proofLocked) {
      setErrorMessage('Blocked by dependency. Proof upload is disabled.')
      return
    }
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Only image proofs are supported.')
      return
    }
    if (file.size > MAX_PROOF_BYTES) {
      setErrorMessage('Proof must be 10MB or smaller.')
      return
    }
    setUploading(true)
    setErrorMessage(null)
    try {
      await submitHelperProof({
        item: {
          id: activeItem.id,
          workspaceId: activeItem.workspaceId,
          title: activeItem.title,
          updatedAtIso: activeItem.updatedAtIso ?? null,
        },
        actor,
        file,
        pageKey: recurringMode ? selectedOccurrenceKey : null,
        actorRole,
      })
      proofsCache.delete(activeItem.id)
      setProofsSyncToken((current) => current + 1)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Upload failed.')
    } finally {
      setUploading(false)
    }
  })

  const handleReceiptFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Only receipt photos are supported.')
      return
    }
    if (file.size > MAX_PROOF_BYTES) {
      setErrorMessage('Receipt must be 10MB or smaller.')
      return
    }
    setReceiptFile(file)
    setErrorMessage(null)
  }

  const handleReceiptSubmit = withLock(async () => {
    if (!actor || !receiptFile) return
    setReceiptBusy(true)
    setErrorMessage(null)
    try {
      await submitPendingExpense({
        item: activeItem,
        actor,
        file: receiptFile,
        amountMinor: parseExpenseAmountToMinor(receiptAmount),
        currencyCode: receiptCurrency,
        note: receiptNote,
        category: (activeItem as { budgetCategory?: string | null }).budgetCategory,
      })
      setReceiptFile(null)
      setReceiptAmount('')
      setReceiptNote('')
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Receipt could not be queued.')
    } finally {
      setReceiptBusy(false)
    }
  })

  const submitEscalation = withLock(async () => {
    if (!actor) return
    const reason = escalateDraft.trim()
    if (!reason) {
      setErrorMessage('Add a short reason so members know what to look at.')
      return
    }
    setEscalateBusy(true)
    setErrorMessage(null)
    try {
      await submitHelperComment({
        item: {
          id: activeItem.id,
          workspaceId: activeItem.workspaceId,
          parentContainerId: activeItem.parentContainerId,
          updatedAtIso: activeItem.updatedAtIso ?? null,
        },
        actor,
        body: `[ESCALATION] ${reason}`,
        pageKey: recurringMode ? selectedOccurrenceKey : null,
        actorRole,
      })
      setEscalateDraft('')
      setEscalateOpen(false)
      commentsCache.delete(activeItem.id)
      setCommentsSyncToken((current) => current + 1)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Escalation could not be sent.')
    } finally {
      setEscalateBusy(false)
    }
  })

  const commentsLastLoadedAt = commentsCache.get(activeItem.id)?.loadedAt ?? null
  const proofsLastLoadedAt = proofsCache.get(activeItem.id)?.loadedAt ?? null
  const formsLastLoadedAt = formsCache.get(formsCacheKey)?.loadedAt ?? null
  const designsLastLoadedAt = designsCache.get(designsCacheKey)?.loadedAt ?? null

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] isolate overflow-y-auto overscroll-contain bg-[var(--bg)]"
      >
        {/* eslint-disable-next-line no-restricted-syntax -- full-viewport helper checklist details sheet with gesture physics is not a Common modal */}
        <motion.section role="dialog"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 240, damping: 30 }}
          className="relative flex min-h-full flex-col text-[var(--text-strong)]"
          aria-modal="true"
          aria-label={t('NLSHelperChecklistDetailsAria')}
          data-testid="helper-checklist-modal"
        >
          <header
            className="sticky top-0 z-10 flex shrink-0 items-center gap-3 border-b border-[var(--border-subtle)] px-4 py-4 backdrop-blur-2xl"
            style={{
              paddingTop: 'max(1rem, env(safe-area-inset-top))',
              backgroundColor: 'color-mix(in srgb, var(--surface) 85%, transparent)',
            }}
          >
            <IconButton
              variant="tertiary"
              size="md"
              onClick={handleClose}
              label="Back"
              data-testid="helper-checklist-back"
              icon={<ArrowBackIosNewRoundedIcon fontSize="small" />}
            />
            <div className="min-w-0 flex-1">
              <p className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${STATUS_TONE[currentStatus]}`}>
                {STATUS_LABEL[currentStatus]}
              </p>
              <h2 className="truncate text-base font-semibold">{activeItem.title}</h2>
              {dependencyBlocked ? (
                <p className="flex items-center gap-1 truncate text-[11px] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
                  <LockRoundedIcon style={{ fontSize: 12 }} />
                  Blocked by dependency
                </p>
              ) : null}
            </div>
            {editLock.holder && !editLock.heldByMe ? (
              <Button
                variant="secondary"
                size="md"
                onClick={() => void editLock.recheck()}
                disabled={editLock.isCheckingLock}
                title={`Locked by ${editLock.holder}. Click to re-check.`}
                icon={<RefreshRoundedIcon fontSize="small" className={editLock.isCheckingLock ? 'spin' : ''} />}
              >
                Re-check
              </Button>
            ) : null}
          </header>

          <div
            className="flex flex-col gap-4 px-4 pb-40 pt-4"
            style={{ paddingBottom: 'calc(9rem + env(safe-area-inset-bottom, 0px))' }}
            data-testid="helper-checklist-scroll-body"
          >
            <StatusBlockerBanner
              pendingStatus={pendingStatus}
              blockers={pendingBlockers}
              onDismiss={() => {
                setPendingStatus(null)
                setServerBlockers([])
              }}
              onBlockerClick={(section) => {
                document.getElementById(`helper-checklist-section-${section}`)?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
              }}
            />
            <section
              className="rounded-[30px] border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]"
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--muted)]">Assigned execution</p>
                <h3 className="mt-1 text-lg font-semibold text-[var(--text-strong)]">{activeItem.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--text)]">
                  Everything here stays helper-only. Sections load when opened, stay cached in the app, and refresh periodically instead of reloading on every view.
                </p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {lock?.isLocked && (
                  <DependencyLockChip isLocked={lock.isLocked} reasons={lock.reasons} />
                )}
                {lock?.reasons.some((r) => r.missedPeriods && r.missedPeriods.length > 0) && (
                  <MissedPeriodsChip
                    missedPeriods={lock.reasons.find((r) => r.missedPeriods && r.missedPeriods.length > 0)?.missedPeriods}
                  />
                )}
                <MetricPill
                  icon={statusIconFor(currentStatus)}
                  label={STATUS_LABEL[currentStatus]}
                  ariaLabel={t('NLSHelperChecklistLiveStatus')}
                  testId="helper-checklist-live-status-pill"
                />
                {scheduleWindow ? <MetricPill icon={<EventRoundedIcon style={{ fontSize: 16 }} />} label={scheduleWindow} /> : null}
                {timeWindow ? <MetricPill icon={<ScheduleRoundedIcon style={{ fontSize: 16 }} />} label={timeWindow} /> : null}
                {activeItem.priority ? <MetricPill icon={<PriorityHighRoundedIcon style={{ fontSize: 16 }} />} label={`Priority ${activeItem.priority}`} /> : null}
                {proofRequired ? <MetricPill icon={<AttachFileRoundedIcon style={{ fontSize: 16 }} />} label={proofs.length > 0 ? 'Proof attached' : 'Proof needed'} /> : null}
                {locationRequired ? <MetricPill icon={<PlaceRoundedIcon style={{ fontSize: 16 }} />} label={activeItem.verifiedCoordinates ? 'Location verified' : 'Location needed'} /> : null}
                {coSignRequired ? <MetricPill icon={<VerifiedUserRoundedIcon style={{ fontSize: 16 }} />} label={activeItem.coSignedByName ?? 'Co-sign needed'} /> : null}
              </div>
            </section>

            {readiness ? (
              <HelperReadinessStrip
                readiness={readiness}
                onReportBlocker={activeItem ? () => onReportBlocker?.(activeItem) : undefined}
                onRequestReplenishment={activeItem ? () => onRequestReplenishment?.(activeItem) : undefined}
              />
            ) : null}

            {recurringMode && occurrenceOptions.length > 0 ? (
              <SectionPanel
                icon={<RepeatRoundedIcon style={{ fontSize: 18 }} />}
                title={t('NLSHelperChecklistRecurringTitle')}
                subtitle={t('NLSHelperChecklistRecurringSubtitle')}
                badge={(
                  <span className="rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-1 text-[11px] text-[var(--muted)]">
                    {recurringMode === 'DAILY' ? 'Daily' : 'Weekly'}
                  </span>
                )}
              >
                <Select
                  ariaLabel="Recurring checklist occurrence"
                  value={selectedOccurrenceKey}
                  trigger="labeledPill"
                  className="helperRecurringOccurrenceSelect"
                  searchable={occurrenceOptions.length >= 8}
                  options={occurrenceOptions.map((option) => ({
                    value: option.key,
                    label: (
                      <span className="memberChecklistOccurrenceOption">
                        <strong>{option.label}</strong>
                        <small>{option.meta}</small>
                      </span>
                    ),
                    searchText: `${option.label} ${option.meta}`,
                  }))}
                  onChange={setSelectedOccurrenceKey}
                />
                {activeOccurrenceMeta ? (
                  <p className="mt-3 text-xs text-[var(--muted)]">{activeOccurrenceMeta.meta}</p>
                ) : null}
              </SectionPanel>
            ) : null}

            <SectionPanel
              icon={<AutoAwesomeRoundedIcon style={{ fontSize: 18 }} />}
              title={t('NLSHelperChecklistBriefTitle')}
              subtitle={t('NLSHelperChecklistBriefSubtitle')}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                {(() => {
                  const assigneeDisplay = displayNameOrMaskedPhone(activeItem.assigneeName, (activeItem as { assigneePhone?: string | null }).assigneePhone)
                  return assigneeDisplay ? (
                    <MetricPill icon={<CheckCircleRoundedIcon style={{ fontSize: 16 }} />} label={`Assigned to ${assigneeDisplay}`} />
                  ) : null
                })()}
                {dependencyBlocked ? <MetricPill icon={<LockRoundedIcon style={{ fontSize: 16 }} />} label="Dependency is blocking this item" /> : null}
                {activeItem.memoUploadedByName ? <MetricPill icon={<BrushRoundedIcon style={{ fontSize: 16 }} />} label={`Memo from ${activeItem.memoUploadedByName}`} /> : null}
                {activeItem.proofVideoUrl ? <MetricPill icon={<VisibilityRoundedIcon style={{ fontSize: 16 }} />} label="Walkthrough video attached" /> : null}
              </div>
            </SectionPanel>

            {(activeItem.description || activeItem.completionCriteria) ? (
              <SectionPanel
                icon={<DescriptionRoundedIcon style={{ fontSize: 18 }} />}
                title={t('NLSHelperChecklistInstructionsTitle')}
                subtitle={t('NLSHelperChecklistInstructionsSubtitle')}
              >
                <div className="flex flex-col gap-4">
                  {activeItem.description ? (
                    <div
                      className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-4"
                    >
                      <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">Description</p>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-strong)]">{activeItem.description}</p>
                    </div>
                  ) : null}
                  {activeItem.completionCriteria ? (
                    <div
                      className="rounded-3xl border border-[color-mix(in_srgb,var(--success)_20%,transparent)] bg-[var(--success-soft)] p-4"
                    >
                      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]">
                        <FactCheckRoundedIcon style={{ fontSize: 14 }} />
                        Done when
                      </p>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-strong)]">{activeItem.completionCriteria}</p>
                    </div>
                  ) : null}
                </div>
              </SectionPanel>
            ) : null}

            {(activeItem.attachedFormIds?.length ?? 0) > 0 ? (
              <SectionPanel
                icon={<FactCheckRoundedIcon style={{ fontSize: 18 }} />}
                title={t('NLSHelperChecklistFormsTitle')}
                subtitle={t('NLSHelperChecklistFormsSubtitle')}
                badge={<span className="text-xs text-[var(--muted)]">{forms.length || activeItem.attachedFormIds?.length || 0}</span>}
                loading={formsLoading}
              >
                <div className="flex items-center justify-between gap-3 text-xs text-[var(--muted)]">
                  <span>{formatRefreshLabel(formsLastLoadedAt)}</span>
                  <Button
                    variant="tertiary"
                    size="md"
                    icon={<RefreshRoundedIcon style={{ fontSize: 14 }} />}
                    onClick={() => {
                      formsCache.delete(formsCacheKey)
                      setFormsSyncToken((current) => current + 1)
                    }}
                  >
                    Refresh
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {forms.map((form) => (
                    <Pressable
                      key={form.id}
                      onClick={() => setSelectedFormId(form.id)}
                      aria-pressed={selectedFormId === form.id}
                      className={`rounded-2xl border px-3 py-2 text-left text-sm transition ${
                        selectedFormId === form.id ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--text-strong)]' : 'border-[var(--border-subtle)] bg-[var(--surface-muted)] text-[var(--text)]'
                      }`}
                    >
                      <div className="font-medium">{form.title}</div>
                      <div className="mt-1 text-xs text-[var(--muted)]">{form.fields.length} fields · {form.lifecycleStatus}</div>
                    </Pressable>
                  ))}
                </div>
                {activeForm ? (
                  <div className="mt-4 rounded-[28px] border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3">
                    <FormExperienceScreen
                      definition={activeForm}
                      initialMode="FILL"
                      canRespond={activeForm.lifecycleStatus === 'ACTIVE' && activeForm.isLocked !== true}
                      canReviewResponses={false}
                      compact
                      onBack={() => setSelectedFormId(null)}
                      onSubmitted={() => setSelectedFormId(null)}
                    />
                  </div>
                ) : null}
              </SectionPanel>
            ) : null}

            {(activeItem.attachedDesignIds?.length ?? 0) > 0 ? (
              <SectionPanel
                icon={<PaletteRoundedIcon style={{ fontSize: 18 }} />}
                title={t('NLSHelperChecklistDesignsTitle')}
                subtitle={t('NLSHelperChecklistDesignsSubtitle')}
                badge={<span className="text-xs text-[var(--muted)]">{designs.length || activeItem.attachedDesignIds?.length || 0}</span>}
                loading={designsLoading}
              >
                <div className="flex items-center justify-between gap-3 text-xs text-[var(--muted)]">
                  <span>{formatRefreshLabel(designsLastLoadedAt)} · Preview only</span>
                  <Button
                    variant="tertiary"
                    size="md"
                    icon={<RefreshRoundedIcon style={{ fontSize: 14 }} />}
                    onClick={() => {
                      designsCache.delete(designsCacheKey)
                      setDesignsSyncToken((current) => current + 1)
                    }}
                  >
                    Refresh
                  </Button>
                </div>
                <div className="mt-3 grid gap-3">
                  {designs.map((design) => (
                    <Pressable
                      key={design.id}
                      onClick={() => setActiveDesignPreview(design)}
                      className="overflow-hidden rounded-[24px] border border-[var(--border-subtle)] bg-[var(--surface-muted)] text-left transition hover:border-[var(--accent)]"
                    >
                      <div className="border-b border-[var(--border-subtle)] px-4 py-3">
                        <p className="text-sm font-semibold text-[var(--text-strong)]">{design.asset.title}</p>
                        <p className="mt-1 text-xs text-[var(--muted)]">Tap to open cached preview</p>
                      </div>
                      <div className="flex items-center justify-center p-4">
                        {design.snapshot ? (
                          <DesignSnapshotRenderer snapshot={design.snapshot} maxWidth={280} maxHeight={160} />
                        ) : (
                          <div className="flex h-32 w-full items-center justify-center rounded-2xl border border-dashed border-[var(--border-subtle)] text-xs text-[var(--muted)]">
                            No rendered preview available yet
                          </div>
                        )}
                      </div>
                    </Pressable>
                  ))}
                </div>
              </SectionPanel>
            ) : null}

            <SectionPanel
              id="helper-checklist-section-proof"
              icon={<AttachFileRoundedIcon style={{ fontSize: 18 }} />}
              title={t('NLSHelperChecklistProofTitle')}
              subtitle={t('NLSHelperChecklistProofSubtitle')}
              badge={<span className="text-xs text-[var(--muted)]">{proofs.length}</span>}
              loading={proofsLoading}
            >
              <div className="flex items-center justify-between gap-3 text-xs text-[var(--muted)]">
                <span>{formatRefreshLabel(proofsLastLoadedAt)}</span>
                <Button
                  variant="tertiary"
                  size="md"
                  icon={<RefreshRoundedIcon style={{ fontSize: 14 }} />}
                  onClick={() => {
                    proofsCache.delete(activeItem.id)
                    setProofsSyncToken((current) => current + 1)
                  }}
                >
                  Refresh
                </Button>
              </div>
              {proofs.length === 0 ? (
                <p className="mt-3 text-xs text-[var(--muted)]">{proofRequired ? 'Photo evidence required before completion.' : 'No proof submitted yet.'}</p>
              ) : (
                <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
                  {proofs.map((proof) => {
                    const itemForPreview = buildPreviewItem(proof)
                    return (
                      <Pressable
                        key={proof.id}
                        className="w-36 shrink-0 overflow-hidden rounded-[24px] border border-[var(--border-subtle)] bg-[var(--surface-muted)] text-left"
                        onClick={() => itemForPreview && setPreviewItem(itemForPreview)}
                      >
                        <div className="flex h-28 items-center justify-center overflow-hidden bg-[var(--surface-card)]">
                          {proof.fileUrl ? (
                            <img src={proof.fileUrl} alt={proof.fileName ?? 'proof'} className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-xs text-[var(--muted)]">Preview</span>
                          )}
                        </div>
                        <div className="px-3 py-3">
                          <p className="truncate text-sm font-medium text-[var(--text-strong)]">{proof.fileName ?? 'Proof image'}</p>
                          <p className="mt-1 text-xs text-[var(--muted)]">
                            {proof.uploadedByName ?? 'Helper'}
                            {proof.uploadedAt ? ` · ${formatDateTime(proof.uploadedAt)}` : ''}
                          </p>
                        </div>
                      </Pressable>
                    )
                  })}
                </div>
              )}
              {(activeItem.voiceMemoBlobUrl || activeItem.proofVideoUrl || activeItem.memoFileName) ? (
                <div className="mt-4 rounded-[24px] border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-4">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">Memos and walkthroughs</p>
                  {activeItem.voiceMemoBlobUrl ? (
                    <audio controls preload="none" className="mt-3 w-full">
                      <source src={activeItem.voiceMemoBlobUrl} type={activeItem.memoContentType ?? 'audio/webm'} />
                    </audio>
                  ) : null}
                  {activeItem.proofVideoUrl ? (
                    <video controls preload="metadata" className="mt-3 w-full rounded-2xl border border-[var(--border-subtle)]" src={activeItem.proofVideoUrl} />
                  ) : null}
                  <p className="mt-3 text-xs text-[var(--muted)]">
                    {activeItem.memoFileName ?? 'A memo or walkthrough has been attached to this checklist.'}
                    {activeItem.memoUploadedByName ? ` Uploaded by ${activeItem.memoUploadedByName}.` : ''}
                  </p>
                </div>
              ) : null}
              <div className="mt-4 flex flex-wrap gap-2">
                <label
                  className={`inline-flex items-center gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-4 py-3 text-sm font-medium transition ${proofLocked || inputsDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[var(--surface-hover)]'}`}
                >
                  {/* eslint-disable-next-line no-restricted-syntax -- mobile camera environment capture unsupported by Common HiddenFileInput */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={proofLocked || inputsDisabled}
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      if (file) void handleProof(file)
                      event.target.value = ''
                    }}
                  />
                  <CameraAltRoundedIcon fontSize="small" />
                  {proofLocked ? 'Proof locked' : inputsDisabled ? 'Locked by peer' : uploading ? <MiniLoader tone="upload" size="xs" label="Queueing proof" inline /> : 'Snap proof'}
                </label>
                <HiddenFileInput
                  ref={deviceFileInputRef}
                  accept="image/*"
                  disabled={proofLocked || inputsDisabled}
                  onFiles={(files) => {
                    const file = files[0]
                    if (file) void handleProof(file)
                  }}
                />
                <Button
                  variant="tertiary"
                  size="md"
                  icon={<AttachFileRoundedIcon fontSize="small" />}
                  onClick={() => deviceFileInputRef.current?.click()}
                  disabled={proofLocked || inputsDisabled}
                >
                  Attach from device
                </Button>
                <label
                  className={`inline-flex items-center gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-4 py-3 text-sm font-medium transition ${inputsDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[var(--surface-hover)]'}`}
                >
                  {/* eslint-disable-next-line no-restricted-syntax -- mobile camera environment capture unsupported by Common HiddenFileInput */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={inputsDisabled}
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      if (file) handleReceiptFile(file)
                      event.target.value = ''
                    }}
                  />
                  <ReceiptLongRoundedIcon fontSize="small" />
                  Attach receipt
                </label>
              </div>
              {receiptFile ? (
                <div className="mt-4 rounded-3xl border border-[color-mix(in_srgb,var(--success)_20%,transparent)] bg-[var(--success-soft)] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]">Pending receipt</p>
                      <h4 className="mt-1 truncate text-sm font-semibold text-[var(--text-strong)]">{receiptFile.name}</h4>
                    </div>
                    <Button
                      variant="tertiary"
                      size="md"
                      onClick={() => setReceiptFile(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="mt-3 grid grid-cols-[1fr_6rem] gap-2">
                    <label className="text-xs text-[var(--muted)]">
                      Amount
                      <TextInput
                        value={receiptAmount}
                        disabled={receiptBusy || inputsDisabled}
                        inputMode="decimal"
                        onChange={setReceiptAmount}
                        placeholder="0.00"
                        size="sm"
                        wrapperClassName="mt-1"
                      />
                    </label>
                    <label className="text-xs text-[var(--muted)]">
                      Currency
                      <TextInput
                        value={receiptCurrency}
                        disabled={receiptBusy || inputsDisabled}
                        maxLength={3}
                        onChange={(value) => setReceiptCurrency(value.toUpperCase())}
                        size="sm"
                        wrapperClassName="mt-1"
                      />
                    </label>
                  </div>
                  <div className="mt-3 text-xs text-[var(--muted)]">
                    <span>Note</span>
                    <FloatingInput label="Receipt notes">
                      <Textarea
                        value={receiptNote}
                        disabled={receiptBusy || inputsDisabled}
                        onChange={setReceiptNote}
                        rows={2}
                        wrapperClassName="mt-1"
                      />
                    </FloatingInput>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    className="mt-3"
                    disabled={receiptBusy || inputsDisabled}
                    onClick={() => { void handleReceiptSubmit() }}
                  >
                    {receiptBusy ? <MiniLoader tone="upload" size="xs" label="Queueing receipt" inline /> : 'Queue receipt'}
                  </Button>
                </div>
              ) : null}
            </SectionPanel>

            <SectionPanel
              icon={<ChatBubbleRoundedIcon style={{ fontSize: 18 }} />}
              title={t('NLSHelperChecklistCommentsTitle')}
              subtitle={t('NLSHelperChecklistCommentsSubtitle')}
              badge={<span className="text-xs text-[var(--muted)]">{comments.length}</span>}
              loading={commentsLoading}
            >
              <div className="flex items-center justify-between gap-3 text-xs text-[var(--muted)]">
                <span>{formatRefreshLabel(commentsLastLoadedAt)}</span>
                <Button
                  variant="tertiary"
                  size="md"
                  icon={<RefreshRoundedIcon style={{ fontSize: 14 }} />}
                  onClick={() => {
                    commentsCache.delete(activeItem.id)
                    setCommentsSyncToken((current) => current + 1)
                  }}
                >
                  Refresh
                </Button>
              </div>

              {comments.length === 0 ? (
                <p className="mt-3 text-xs text-[var(--muted)]">No comments yet.</p>
              ) : (
                <ul className="mt-3 flex list-none flex-col gap-3 p-0">
                  {comments.map((comment) => {
                    const isOwn = Boolean(actor && comment.actorId === actor.id)
                    const isEscalation = comment.body.startsWith('[ESCALATION]')
                    const isDeleted = Boolean(comment.deletedAt)
                    return (
                      <li
                        key={comment.id}
                        className={`rounded-[24px] border px-4 py-3 ${isEscalation ? 'border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[var(--danger-soft)]' : 'border-[var(--border-subtle)] bg-[var(--surface-muted)]'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[var(--text-strong)]">
                              {isEscalation ? 'Escalation · ' : ''}
                              {comment.actorName}
                            </p>
                            <p className="mt-1 text-[11px] text-[var(--muted)]">
                              {formatDateTime(comment.createdAt)}
                              {comment.editedAt ? ' · edited' : ''}
                            </p>
                          </div>
                          {isOwn && !isDeleted ? (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="tertiary"
                                size="md"
                                icon={<EditRoundedIcon style={{ fontSize: 13 }} />}
                                disabled={commentLocked || postingComment || inputsDisabled}
                                onClick={() => {
                                  setEditingCommentId(comment.id)
                                  setEditingCommentDraft(extractEscalationBody(comment.body))
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="tertiary"
                                tone="danger"
                                size="md"
                                icon={<DeleteOutlineRoundedIcon style={{ fontSize: 13 }} />}
                                disabled={commentLocked || postingComment || inputsDisabled}
                                onClick={() => setCommentToDelete(comment)}
                              >
                                Delete
                              </Button>
                            </div>
                          ) : null}
                        </div>

                        {editingCommentId === comment.id ? (
                          <div className="mt-3 flex flex-col gap-2">
                            <FloatingInput label="Comment">
                              <Textarea
                                value={editingCommentDraft}
                                disabled={inputsDisabled}
                                onChange={setEditingCommentDraft}
                                rows={3}
                              />
                            </FloatingInput>
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="tertiary"
                                size="md"
                                onClick={() => {
                                  setEditingCommentId(null)
                                  setEditingCommentDraft('')
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                size="md"
                                disabled={!editingCommentDraft.trim() || postingComment || inputsDisabled}
                                onClick={() => void handleSaveCommentEdit()}
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-strong)]">
                            {isDeleted ? 'Comment deleted' : isEscalation ? extractEscalationBody(comment.body) : comment.body}
                          </p>
                        )}

                        {!isDeleted && comment.attachments.length > 0 ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {comment.attachments.map((attachment) => {
                              const itemForPreview = buildPreviewItem(attachment)
                              return (
                                <Pressable
                                  key={attachment.id}
                                  className="inline-flex items-center gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-left text-xs text-[var(--text-strong)]"
                                  onClick={() => itemForPreview && setPreviewItem(itemForPreview)}
                                >
                                  <AttachFileRoundedIcon style={{ fontSize: 14 }} />
                                  <span className="max-w-[180px] truncate">{attachment.name}</span>
                                </Pressable>
                              )
                            })}
                          </div>
                        ) : null}
                      </li>
                    )
                  })}
                </ul>
              )}

              <div className="mt-4 flex gap-2">
                <TextInput
                  value={commentDraft}
                  onChange={setCommentDraft}
                  disabled={commentLocked || postingComment || inputsDisabled}
                  placeholder={commentLocked ? 'Comments locked' : inputsDisabled ? 'Locked by peer' : 'Log an update…'}
                  wrapperClassName="flex-1"
                />
                <Button
                  variant="primary"
                  size="lg"
                  icon={<SendRoundedIcon style={{ fontSize: 16 }} />}
                  onClick={() => void handleComment()}
                  disabled={!commentDraft.trim() || commentLocked || postingComment || inputsDisabled}
                >
                  Post
                </Button>
              </div>
            </SectionPanel>

            <SectionPanel
              icon={<CheckCircleRoundedIcon style={{ fontSize: 18 }} />}
              title={t('NLSHelperChecklistStatusTitle')}
              subtitle={t('NLSHelperChecklistStatusSubtitle')}
            >
              {activeAllowedTransitions.length === 0 ? (
                <p className="text-xs text-[var(--muted)]">No further transitions available from {STATUS_LABEL[currentStatus]}.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {activeAllowedTransitions.map((next) => {
                    const evalResult = evalTarget ? canPickInDropdown(evalTarget, actor ? { uid: actor.id, role: actorRole } : null, next) : { ok: true }
                    const isGated = !evalResult.ok
                    return (
                      <Button
                        key={next}
                        variant={next === 'COMPLETED' ? 'primary' : 'tertiary'}
                        size="md"
                        onClick={() => void handleStatus(next)}
                        disabled={submittingStatus !== null || statusLocked || (next === 'COMPLETED' && completionLocked) || inputsDisabled}
                        title={isGated ? evalResult.reason : undefined}
                        data-testid={`helper-status-${next.toLowerCase()}`}
                      >
                        {submittingStatus === next ? '…' : <><ArrowForwardRoundedIcon fontSize="inherit" /> {STATUS_LABEL[next]}</>}
                        {isGated && (
                          <LockRoundedIcon
                            fontSize="inherit"
                            style={{ fontSize: 14, opacity: 0.7 }}
                            aria-hidden="true"
                          />
                        )}
                      </Button>
                    )
                  })}
                </div>
              )}
            </SectionPanel>

            {errorMessage ? (
              <div className="flex items-start gap-2 rounded-2xl border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[var(--danger-soft)] px-3 py-2 text-xs text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]" role="status">
                <ErrorOutlineRoundedIcon fontSize="small" />
                <span>{errorMessage}</span>
              </div>
            ) : null}
          </div>

          <div
            className="fixed z-20 flex items-center gap-2 border border-[var(--border-subtle)] backdrop-blur-2xl"
            style={{
              left: 12,
              right: 12,
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
              padding: '10px 12px',
              borderRadius: 18,
              backgroundColor: 'color-mix(in srgb, var(--surface) 92%, transparent)',
              boxShadow: 'var(--shadow-pop)',
            }}
          >
            {canDelegateFieldPass ? (
              <Button
                variant="secondary"
                size="lg"
                className="shrink-0"
                icon={<LocalOfferRoundedIcon fontSize="small" />}
                onClick={() => setFieldPassDelegateOpen(true)}
                disabled={!actor || inputsDisabled}
                aria-label={t('NLSHelperFieldPassDelegate')}
                title={t('NLSHelperFieldPassDelegate')}
                data-testid="helper-field-pass-delegate"
              >
                <span className="hidden sm:inline">{t('NLSHelperFieldPassDelegateShort')}</span>
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="lg"
              icon={<ReportGmailerrorredRoundedIcon fontSize="small" />}
              onClick={() => setEscalateOpen(true)}
              disabled={!actor || inputsDisabled}
              data-testid="helper-escalate"
            >
              Escalate
            </Button>
            <Button
              variant="primary"
              size="lg"
              className="flex-1"
              icon={<CheckCircleRoundedIcon fontSize="small" />}
              onClick={() => void handleStatus('COMPLETED')}
              disabled={!canMarkDone || submittingStatus === 'COMPLETED' || inputsDisabled}
              data-testid="helper-mark-done"
            >
              {submittingStatus === 'COMPLETED' ? 'Marking…' : locationRequired && !activeItem.verifiedCoordinates ? 'Check in & done' : 'Mark done'}
            </Button>
          </div>

          <QuickDelegateSheet
            open={fieldPassDelegateOpen}
            workspaceId={activeItem.workspaceId}
            targets={[{ id: activeItem.id, title: activeItem.title }]}
            onClose={() => setFieldPassDelegateOpen(false)}
            onNotice={(message, tone) => {
              if (tone === 'error') setErrorMessage(message)
            }}
          />

          {escalateOpen ? (
            <div
              className="fixed inset-0 z-30 flex items-end justify-center px-4 pb-6 sm:items-center"
              style={{ backgroundColor: 'color-mix(in srgb, var(--bg) 60%, transparent)' }}
              onClick={() => { if (!escalateBusy) setEscalateOpen(false) }}
            >
              <div
                className="flex w-full max-w-sm flex-col gap-3 rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] shadow-[var(--shadow-pop)] p-5"
                onClick={(event) => event.stopPropagation()}
              >
                <div>
                  <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
                    <ReportGmailerrorredRoundedIcon style={{ fontSize: 14 }} />
                    Escalate to member
                  </p>
                  <p className="mt-1 text-xs text-[var(--muted)]">A member will see this flagged in the item&apos;s comment thread.</p>
                </div>
                <FloatingInput label="Blocker details">
                  <Textarea
                    value={escalateDraft}
                    onChange={setEscalateDraft}
                    disabled={escalateBusy || inputsDisabled}
                    placeholder={t('NLSHelperChecklistBlockerPlaceholder')}
                    rows={4}
                  />
                </FloatingInput>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="tertiary"
                    size="md"
                    onClick={() => setEscalateOpen(false)}
                    disabled={escalateBusy || inputsDisabled}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => void submitEscalation()}
                    disabled={escalateBusy || !escalateDraft.trim() || inputsDisabled}
                  >
                    {escalateBusy ? 'Sending…' : 'Send escalation'}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {activeDesignPreview ? (
            <div
              className="fixed inset-0 z-30 flex items-end justify-center overflow-hidden px-4 pb-6 sm:items-center"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--bg) 60%, transparent)',
                paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
              }}
              onClick={() => setActiveDesignPreview(null)}
            >
              <div
                className="flex max-h-[calc(100dvh-3rem)] w-full max-w-3xl flex-col overflow-hidden rounded-[32px] border border-[var(--border-subtle)] bg-[var(--surface-card)] shadow-[var(--shadow-pop)]"
                onClick={(event) => event.stopPropagation()}
                data-testid="helper-design-preview-card"
              >
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--border-subtle)] px-5 py-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">Design preview</p>
                    <h3 className="mt-1 text-base font-semibold text-[var(--text-strong)]">{activeDesignPreview.asset.title}</h3>
                    <p className="mt-1 text-xs text-[var(--muted)]">Cached read-only preview for helper reference.</p>
                  </div>
                  <Button
                    variant="tertiary"
                    size="md"
                    onClick={() => setActiveDesignPreview(null)}
                  >
                    Close
                  </Button>
                </div>
                <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto px-4 py-6" data-testid="helper-design-preview-body">
                  {activeDesignPreview.snapshot ? (
                    <DesignSnapshotRenderer snapshot={activeDesignPreview.snapshot} maxWidth={900} maxHeight={560} />
                  ) : (
                    <div className="flex h-48 w-full items-center justify-center rounded-2xl border border-dashed border-[var(--border-subtle)] text-sm text-[var(--muted)]">
                      No rendered preview available for this design yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </motion.section>

        <AttachmentPreviewDialog
          open={Boolean(previewItem)}
          item={previewItem}
          onClose={() => setPreviewItem(null)}
        />

        <ConfirmActionModal
          open={Boolean(commentToDelete)}
          onClose={() => setCommentToDelete(null)}
          onConfirm={async () => {
            if (!commentToDelete) return
            await handleDeleteComment(commentToDelete)
            setCommentToDelete(null)
          }}
          title={t('NLSHelperCommentDeleteTitle')}
          body={t('NLSHelperCommentDeleteBody')}
          confirmLabel={t('NLSHelperCommentDeleteConfirm')}
          danger
          busy={postingComment}
        />
      </motion.div>
    </AnimatePresence>
  )
}
